import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Image, Play, FileJson } from "lucide-react";
import type { OnboardingPage, TagStyle, LocalizedText } from "@/types/onboarding";

interface OnboardingPreviewProps {
  page?: Partial<OnboardingPage> | null;
  totalPages?: number;
  currentIndex?: number;
  isSkippable?: boolean;
  locale?: string;
}

function lt(value: LocalizedText | string | null | undefined, locale: string): string {
  if (!value) return "";
  if (typeof value === "string") {
    try {
      const parsed = JSON.parse(value);
      if (typeof parsed === "object" && parsed !== null) {
        return parsed[locale] || parsed["en"] || Object.values(parsed)[0] || "";
      }
    } catch {
      // not JSON, use as plain string
    }
    return value;
  }
  return value[locale] || value["en"] || Object.values(value)[0] || "";
}

const tagStyleColors: Record<TagStyle, string> = {
  default: "bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-200",
  success: "bg-green-100 text-green-800 dark:bg-green-900/40 dark:text-green-300",
  warning: "bg-yellow-100 text-yellow-800 dark:bg-yellow-900/40 dark:text-yellow-300",
  info: "bg-blue-100 text-blue-800 dark:bg-blue-900/40 dark:text-blue-300",
};

export default function OnboardingPreview({
  page,
  totalPages = 3,
  currentIndex = 0,
  isSkippable = true,
  locale = "en",
}: OnboardingPreviewProps) {
  const mediaBg = page?.mediaBgColor || undefined;

  return (
    <div className="flex flex-col items-center">
      {/* Phone frame */}
      <div
        className="relative bg-white dark:bg-gray-900 rounded-[2.5rem] shadow-xl border-4 border-gray-800 dark:border-gray-600 overflow-hidden"
        style={{ width: 280, height: 560 }}
      >
        {/* Notch */}
        <div className="absolute top-0 left-1/2 -translate-x-1/2 w-28 h-6 bg-gray-800 dark:bg-gray-600 rounded-b-2xl z-10" />

        {/* Screen content */}
        <div className="flex flex-col h-full pt-8">
          {/* Skip button */}
          {isSkippable && (
            <div className="flex justify-end px-4 py-1">
              <span className="text-xs text-gray-400 dark:text-gray-500 font-medium">Skip</span>
            </div>
          )}

          {/* Media area */}
          <div
            className="flex-1 flex items-center justify-center mx-4 rounded-xl overflow-hidden min-h-0"
            style={{ backgroundColor: mediaBg, maxHeight: 200 }}
          >
            {page?.mediaType === "image" && page?.mediaUrl ? (
              <img
                src={page.mediaUrl}
                alt="Preview"
                className="w-full h-full object-contain"
              />
            ) : page?.mediaType === "lottie" ? (
              <div className="flex flex-col items-center gap-1 text-gray-400 dark:text-gray-500">
                <FileJson className="w-10 h-10" />
                <span className="text-[10px]">Lottie Animation</span>
              </div>
            ) : page?.mediaType === "video" ? (
              <div className="flex flex-col items-center gap-1 text-gray-400 dark:text-gray-500">
                <Play className="w-10 h-10" />
                <span className="text-[10px]">Video</span>
              </div>
            ) : (
              <div className="flex flex-col items-center gap-1 text-gray-300 dark:text-gray-600">
                <Image className="w-10 h-10" />
                <span className="text-[10px]">No media</span>
              </div>
            )}
          </div>

          {/* Text content area */}
          <div className="flex flex-col items-center px-5 py-4 gap-2 flex-shrink-0">
            {/* Tag */}
            {lt(page?.tag, locale) && (
              <span
                className={`inline-block px-2 py-0.5 rounded-full text-[10px] font-medium ${
                  tagStyleColors[(page?.tagStyle as TagStyle) || "default"]
                }`}
              >
                {lt(page?.tag, locale)}
              </span>
            )}

            {/* Title */}
            <h3 className="text-sm font-bold text-gray-900 dark:text-gray-100 text-center leading-tight">
              {lt(page?.title, locale) || "Page Title"}
            </h3>

            {/* Content */}
            <p className="text-[11px] text-gray-500 dark:text-gray-400 text-center leading-relaxed line-clamp-3">
              {lt(page?.content, locale) || "Page description goes here..."}
            </p>
          </div>

          {/* Bottom area */}
          <div className="flex flex-col items-center gap-3 px-5 pb-8 mt-auto flex-shrink-0">
            {/* Dot indicators */}
            <div className="flex gap-1.5">
              {Array.from({ length: totalPages }).map((_, i) => (
                <div
                  key={i}
                  className={`w-1.5 h-1.5 rounded-full ${
                    i === currentIndex ? "bg-blue-500" : "bg-gray-300 dark:bg-gray-600"
                  }`}
                />
              ))}
            </div>

            {/* CTA Button */}
            <button className="w-full py-2 bg-blue-500 dark:bg-blue-600 text-white text-xs font-semibold rounded-lg">
              {lt(page?.buttonText, locale) || "Get Started"}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
